import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, ArrowUpRight, Star } from "lucide-react";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { assets, siteConfig } from "@/data/site";
import {
  fetchPartitionStallsGoogleReviews,
  type LiveGooglePlaceReviews,
  type LiveGoogleReview,
} from "@/lib/googlePlaceReviews";

const AUTO_ADVANCE_MS = 9000;
const LIVE_REVIEW_MAX_ATTEMPTS = 2;
const LIVE_REVIEW_RETRY_DELAY_MS = 750;
const LIVE_REVIEW_TOTAL_TIMEOUT_MS = 20_000;

type ReviewLoadState = "idle" | "loading" | "retrying" | "ready" | "empty" | "unavailable";

function ReviewStars({ rating, label }: { rating: number; label: string }) {
  return (
    <span className="google-review-stars" role="img" aria-label={label}>
      {Array.from({ length: 5 }, (_, index) => (
        <Star
          key={index}
          aria-hidden="true"
          className={rating >= index + 0.5 ? "is-filled" : undefined}
        />
      ))}
    </span>
  );
}

function reviewerInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase()).join("") || "G";
}

function ReviewAuthor({ review }: { review: LiveGoogleReview }) {
  const publishedLabel = review.relativePublishTime || (review.publishTimeIso
    ? new Date(review.publishTimeIso).toLocaleDateString("en-US", { year: "numeric", month: "short" })
    : "Google reviewer");
  const authorContent = (
    <>
      <span className="google-review-card__avatar" aria-hidden="true">
        <span>{reviewerInitials(review.authorName)}</span>
        {review.authorPhotoUri ? (
          <img
            src={review.authorPhotoUri}
            alt=""
            width="48"
            height="48"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={(event) => { event.currentTarget.style.display = "none"; }}
          />
        ) : null}
      </span>
      <span className="google-review-card__identity">
        <strong>{review.authorName}</strong>
        <span>
          {review.relativePublishTime || (review.publishTimeIso
            ? new Date(review.publishTimeIso).toLocaleDateString("en-US", { year: "numeric", month: "short" })
            : "Google reviewer")}
        </span>
      </span>
    </>
  );

  if (!review.authorUri) {
    return <div className="google-review-card__author">{authorContent}</div>;
  }

  return (
    <a
      className="google-review-card__author"
      href={review.authorUri}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${review.authorName}, ${publishedLabel}. Open Google Maps profile in a new tab`}
    >
      {authorContent}
    </a>
  );
}

function GoogleReviewCard({ review }: { review: LiveGoogleReview }) {
  return (
    <article className="google-review-card" data-google-review-card="live">
      <ReviewAuthor review={review} />
      <ReviewStars rating={review.rating} label={`${review.rating.toFixed(1)} out of 5 stars`} />
      {review.text ? (
        <blockquote><p>{review.text}</p></blockquote>
      ) : (
        <p className="google-review-card__no-comment">Rating submitted on Google without a written comment.</p>
      )}
      <a
        className="google-review-card__source"
        href={siteConfig.googleReviews.read}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="View on Google: Partition Stalls reviews in a new tab"
      >
        View on Google <ArrowUpRight aria-hidden="true" />
      </a>
    </article>
  );
}

function StaticReviewFallback({ state }: { state: ReviewLoadState }) {
  return (
    <div className="google-review-fallback" data-google-review-state={state}>
      <div>
        <strong>Current reviews on Google</strong>
        <p>{state === "unavailable"
          ? "Google review data is temporarily unavailable here. The official profile remains available."
          : state === "empty"
            ? "No individual reviews were returned to display here. Read current customer feedback on the official Google profile."
            : "Read customer feedback and see the current rating on the official Partition Stalls profile on Google."}</p>
      </div>
      <div className="google-review-fallback__actions">
        <a href={siteConfig.googleReviews.read} target="_blank" rel="noopener noreferrer" className="button button--outline">Read current reviews on Google <ArrowUpRight aria-hidden="true" /></a>
        <a href={siteConfig.googleReviews.leave} target="_blank" rel="noopener noreferrer" className="button button--primary">Write a Review <ArrowUpRight aria-hidden="true" /></a>
      </div>
    </div>
  );
}

export function GoogleReviewsCarousel() {
  const [place, setPlace] = useState<LiveGooglePlaceReviews | null>(null);
  const [loadState, setLoadState] = useState<ReviewLoadState>("idle");
  const [api, setApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    let active = true;
    let retryTimer: number | null = null;
    // One deadline covers script/library loading, Places lookup, both attempts,
    // and retry delay. Leave the shared managed Maps loader intact for other consumers.
    const deadlineTimer = window.setTimeout(() => {
      if (!active) return;
      active = false;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      setLoadState("unavailable");
    }, LIVE_REVIEW_TOTAL_TIMEOUT_MS);

    const loadReviews = async (attempt: number) => {
      if (!active) return;
      try {
        const request = fetchPartitionStallsGoogleReviews();
        setLoadState("loading");
        const result = await request;
        if (!active) return;
        active = false;
        window.clearTimeout(deadlineTimer);
        setPlace(result);
        setLoadState(result.reviews.length > 0 ? "ready" : "empty");
      } catch {
        if (!active) return;
        if (attempt < LIVE_REVIEW_MAX_ATTEMPTS) {
          setLoadState("retrying");
          retryTimer = window.setTimeout(() => {
            void loadReviews(attempt + 1);
          }, LIVE_REVIEW_RETRY_DELAY_MS);
          return;
        }
        active = false;
        window.clearTimeout(deadlineTimer);
        setLoadState("unavailable");
      }
    };

    void loadReviews(1);
    return () => {
      active = false;
      window.clearTimeout(deadlineTimer);
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!api) return;
    const update = () => {
      setSelectedIndex(api.selectedScrollSnap());
      setSnapCount(api.scrollSnapList().length);
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
      api.off("reInit", update);
    };
  }, [api]);

  const reviews = place?.reviews ?? [];
  const autoAdvancePaused = prefersReducedMotion || isHovered || isFocusWithin || hasInteracted || reviews.length < 2;

  useEffect(() => {
    if (!api || autoAdvancePaused) return;
    const timer = window.setInterval(() => api.scrollNext(), AUTO_ADVANCE_MS);
    return () => window.clearInterval(timer);
  }, [api, autoAdvancePaused]);

  const ratingLabel = useMemo(() => place
    ? `${place.rating.toFixed(1)} out of 5 based on ${new Intl.NumberFormat("en-US").format(place.userRatingCount)} Google reviews`
    : "Current reviews on the official Google profile",
  [place]);

  const markInteraction = () => setHasInteracted(true);

  return (
    <section
      className="home-google-reviews section-line"
      aria-labelledby="home-google-reviews-title"
      data-google-review-feed="authorized-live-places"
      data-google-review-load-state={loadState}
    >
      <div className="home-google-reviews__inner">
        <header className="home-google-reviews__header">
          <span className="kicker">Customer reviews</span>
          <h2 id="home-google-reviews-title">What our customers are saying.</h2>
          <p>Read customer feedback on the official Partition Stalls profile on Google. When available, the current rating and selected reviews appear here directly from Google.</p>
        </header>

        <div className="home-google-reviews__summary" aria-live="polite">
          <img src={assets.googleMapsLogoWhite} alt="Google Maps" width="123" height="19" loading="lazy" />
          {place ? (
            <div className="home-google-reviews__rating" aria-label={ratingLabel}>
              <strong>{place.rating.toFixed(1)}</strong>
              <div>
                <ReviewStars rating={place.rating} label={ratingLabel} />
                <span>Based on {new Intl.NumberFormat("en-US").format(place.userRatingCount)} Google reviews</span>
              </div>
            </div>
          ) : (
            <div className="home-google-reviews__rating home-google-reviews__rating--loading">
              <div><span>{loadState === "loading" ? "Loading current rating" : "Official profile available"}</span></div>
            </div>
          )}
          <a href={siteConfig.googleReviews.leave} target="_blank" rel="noopener noreferrer" className="button button--primary">Write a Review <ArrowUpRight aria-hidden="true" /></a>
        </div>

        {!place || reviews.length === 0 ? (
          <StaticReviewFallback state={loadState} />
        ) : (
          <div
            className="google-review-carousel"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocusCapture={() => setIsFocusWithin(true)}
            onBlurCapture={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsFocusWithin(false);
            }}
            onPointerDown={markInteraction}
          >
            <Carousel opts={{ align: "start", loop: reviews.length > 1, slidesToScroll: 1 }} setApi={setApi} aria-label="Google customer reviews">
              <CarouselContent className="google-review-carousel__track">
                {reviews.map((review, index) => (
                  <CarouselItem className="google-review-carousel__slide" key={`${review.authorName}-${review.publishTimeIso ?? index}`} aria-label={`Review ${index + 1} of ${reviews.length}`}>
                    <GoogleReviewCard review={review} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
            <div className="google-review-carousel__controls">
              <button type="button" onClick={() => { markInteraction(); api?.scrollPrev(); }} aria-label="Show previous Google review"><ArrowLeft aria-hidden="true" /></button>
              <div className="google-review-carousel__dots" aria-label={`Review position ${selectedIndex + 1} of ${Math.max(snapCount, 1)}`}>
                {Array.from({ length: snapCount }, (_, index) => (
                  <button key={index} type="button" className={index === selectedIndex ? "is-active" : undefined} onClick={() => { markInteraction(); api?.scrollTo(index); }} aria-label={`Go to Google review position ${index + 1}`} aria-current={index === selectedIndex ? "true" : undefined} />
                ))}
              </div>
              <button type="button" onClick={() => { markInteraction(); api?.scrollNext(); }} aria-label="Show next Google review"><ArrowRight aria-hidden="true" /></button>
            </div>
          </div>
        )}

        <footer className="home-google-reviews__attribution">
          <p>Reviews supplied by Google Maps and shown in Google’s returned relevance order.</p>
          <div>
            {place?.attributions.map((attribution) => attribution.providerUri ? (
              <a key={`${attribution.provider}-${attribution.providerUri}`} href={attribution.providerUri} target="_blank" rel="noopener noreferrer">{attribution.provider}</a>
            ) : <span key={attribution.provider}>{attribution.provider}</span>)}
            <a href={place?.googleMapsUri || siteConfig.googleReviews.read} target="_blank" rel="noopener noreferrer">Read all reviews on Google <ArrowUpRight aria-hidden="true" /></a>
          </div>
        </footer>
      </div>
    </section>
  );
}
